import React from 'react'
import { useTranslation } from 'react-i18next'
import { Collapse, Typogaphy } from 'vnetwork-ui'
import { FormItem } from 'vnetwork-ui'
import DocIcons from '@/icons/DocIcons'
import {
    SERVICE_CANCELLED_KEY,
    SERVICE_CREATING_KEY,
    SERVICE_CREATING_POC_KEY,
    SERVICE_SUSPEND_KEY,
    TICKET_TYPE_GROUP_MEETING,
} from '../../../constants'
import TicketMeeting from '../../Forms/TicketMeeting'
import PreviewServiceInformation from '../../Forms/PreviewServiceInformation'
import TicketContent from '../../Forms/TicketContent'
import TicketAffected from '../../Forms/TicketAffected'
import SectionHeader from '../section-header'
import { CreateTicketSectionProps } from '../types'
import { Ticket } from '../../../types/response'

interface ContentSectionProps extends CreateTicketSectionProps {
    ticket?: Ticket
}

const ContentSection: React.FC<ContentSectionProps> = ({
    control,
    watch,
    ticket,
}) => {
    const { t } = useTranslation()
    const ticketTypeGroup = watch('ticket_type_group')
    const ticketTypeKey = watch('ticket_type_key')
    const serviceId = watch('service_id')
    const ticketPriority = watch('ticket_priority')

    // Render service date fields based on ticket_type_key
    const renderServiceDateFields = () => {
        if (ticketTypeGroup !== 'service') return null

        // service_creating: Only show service start date
        if (ticketTypeKey === SERVICE_CREATING_KEY) {
            return (
                <div className="mb-4 p-3 bg-blue-50 rounded-md border border-blue-100">
                    <FormItem
                        control={control}
                        name="service_startdate"
                        type="datepicker"
                        label={t('service-start-date')}
                    />
                </div>
            )
        }

        // service_cancelled: Show both, only edit service_duedate
        if (ticketTypeKey === SERVICE_CANCELLED_KEY) {
            return (
                <div className="grid grid-cols-2 gap-4 mb-4 p-3 bg-blue-50 rounded-md border border-blue-100">
                    <FormItem
                        control={control}
                        name="service_startdate"
                        type="datepicker"
                        label={t('service-start-date')}
                        disabled
                    />
                    <FormItem
                        control={control}
                        name="service_duedate"
                        type="datepicker"
                        label={t('service-end-date')}
                    />
                </div>
            )
        }

        // service_suspend: Show start date + suspend date + suspend until
        if (ticketTypeKey === SERVICE_SUSPEND_KEY) {
            return (
                <div className="space-y-3 mb-4 p-3 bg-orange-50 rounded-md border border-orange-100">
                    <div className="grid grid-cols-2 gap-4">
                        <FormItem
                            control={control}
                            name="service_startdate"
                            type="datepicker"
                            label={t('service-start-date')}
                            disabled
                        />
                        <FormItem
                            control={control}
                            name="service_duedate"
                            type="datepicker"
                            label={t('service-suspend-date')}
                        />
                    </div>
                    <FormItem
                        control={control}
                        name="service_suspend_until"
                        type="datepicker"
                        label={`${t('service-suspend-until')} *`}
                    />
                </div>
            )
        }

        // service_creating_poc: POC start + POC end, edit both
        if (ticketTypeKey === SERVICE_CREATING_POC_KEY) {
            return (
                <div className="grid grid-cols-2 gap-4 mb-4 p-3 bg-green-50 rounded-md border border-green-100">
                    <FormItem
                        control={control}
                        name="service_startdate"
                        type="datepicker"
                        label={t('service-poc-start-date')}
                    />
                    <FormItem
                        control={control}
                        name="service_duedate"
                        type="datepicker"
                        label={t('service-poc-end-date')}
                    />
                </div>
            )
        }

        // Default: Show both, edit both
        return (
            <div className="grid grid-cols-2 gap-4 mb-4 p-3 bg-blue-50 rounded-md border border-blue-100">
                <FormItem
                    control={control}
                    name="service_startdate"
                    type="datepicker"
                    label={t('service-start-date')}
                />
                <FormItem
                    control={control}
                    name="service_duedate"
                    type="datepicker"
                    label={t('service-end-date')}
                />
            </div>
        )
    }

    return (
        <div className="bg-white rounded-md p-4 border border-gray-100">
            <SectionHeader
                step={3}
                title={t('ticket-content-section')}
                icon={<DocIcons className="w-4 h-4" />}
            />

            {/* Meeting fields */}
            {ticketTypeGroup === TICKET_TYPE_GROUP_MEETING && (
                <TicketMeeting
                    control={control}
                    ticketTypeKey={ticketTypeKey}
                />
            )}

            {/* Service date fields */}
            {renderServiceDateFields()}

            {/* Preview Service Information */}
            {ticketTypeGroup === 'service' &&
                ticketTypeKey &&
                typeof serviceId === 'number' &&
                (serviceId ?? 0) > 0 && (
                    <div className="mb-4">
                        <PreviewServiceInformation
                            serviceId={serviceId}
                            ticketTypeKey={ticketTypeKey}
                        />
                    </div>
                )}

            {/* Ticket content */}
            <div data-tour="ticket-content">
                <TicketContent control={control} ticket={ticket} />
            </div>

            {/* Affected section for error_service_incident (high priority or has existing data) */}
            <Collapse
                open={
                    ticketTypeKey === 'error_service_incident' &&
                    (ticketPriority === 'high' || !!ticket?.ticket_affected)
                }
            >
                <div className="mt-2 px-4 py-2 bg-red-50 rounded-md border border-red-100">
                    <div className="flex items-center gap-2 text-red-700">
                        <svg
                            className="w-4 h-4"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                            />
                        </svg>
                        <Typogaphy size="small" className="font-medium">
                            {t('ticket-affected-info')}
                        </Typogaphy>
                    </div>
                    <TicketAffected control={control} />
                </div>
            </Collapse>
        </div>
    )
}

export default ContentSection
